import DOMPurify from 'dompurify';

import {
  DEFAULT_COL_WIDTH,
  MIN_COL_WIDTH,
} from 'components/form/rte/utils/tableConstants';

const { sanitize: dompurifySanitize } = DOMPurify;

const defaultConfig = {
  ALLOWED_TAGS: [
    'p',
    'ul',
    'ol',
    'li',
    'strong',
    'b',
    'em',
    'u',
    'mark',
    'pre',
    'code',
    'blockquote',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'div',
    'span',
    'br',
    'hr',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'colgroup',
    'col',
  ],
  ALLOWED_ATTR: ['colspan', 'rowspan', 'colwidth'],
};

const richContentConfig = {
  ALLOWED_TAGS: [...defaultConfig.ALLOWED_TAGS, 'a', 's', 'del', 'strike', 'i'],
  ALLOWED_ATTR: [...defaultConfig.ALLOWED_ATTR, 'href'],
};

export const sanitize = (val: string, config = defaultConfig): string => {
  const html = dompurifySanitize(val, {
    ...defaultConfig,
    ...config,
  }) as string;
  return convertTableWidths(html);
};

export const sanitizeRichContent = (val: string): string =>
  prepareRichContent(sanitize(val, richContentConfig));

function columnCount(table: HTMLTableElement): number {
  return Array.from(table.rows).reduce((widest, row) => {
    const columns = Array.from(row.cells).reduce(
      (total, cell) => total + cell.colSpan,
      0
    );

    return Math.max(widest, columns);
  }, 0);
}

function prepareRichContent(html: string): string {
  if (!html.includes('<a') && !html.includes('<table')) return html;
  if (typeof document === 'undefined') return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  const fragment = template.content;

  fragment.querySelectorAll('a').forEach((anchor) => {
    const href = anchor.getAttribute('href') || '';

    if (!href) {
      anchor.replaceWith(...Array.from(anchor.childNodes));
      return;
    }

    if (!/^(mailto:|tel:)/i.test(href)) anchor.setAttribute('target', '_blank');

    anchor.setAttribute('rel', 'noopener noreferrer nofollow');
  });

  fragment.querySelectorAll('table').forEach((table) => {
    const columns = columnCount(table);
    if (columns === 0) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'jobTableScroll';

    table.style.setProperty('min-width', `${columns * MIN_COL_WIDTH}px`);
    table.replaceWith(wrapper);
    wrapper.appendChild(table);
  });

  const div = document.createElement('div');
  div.appendChild(fragment);
  return div.innerHTML;
}

function convertTableWidths(html: string): string {
  if (!html.includes('<table')) return html;
  if (typeof document === 'undefined') return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  const fragment = template.content;

  fragment.querySelectorAll('table').forEach((table) => {
    const firstRow = table.querySelector('tr');
    if (!firstRow) return;

    const cells = Array.from(firstRow.querySelectorAll('td, th'));
    const rawWidths = cells.map((cell) => {
      const colwidthAttr = cell.getAttribute('colwidth') || '';
      const width = colwidthAttr
        .split(',')
        .map((v) => parseInt(v, 10))
        .filter((v) => v > 0)
        .reduce((sum, v) => sum + v, 0);
      return width > 0 ? width : 0;
    });

    const hasAnyColwidth = rawWidths.some((w) => w > 0);
    if (!hasAnyColwidth) return;

    const effectiveWidths = rawWidths.map((w) =>
      w > 0 ? w : DEFAULT_COL_WIDTH
    );
    const total = effectiveWidths.reduce((sum, w) => sum + w, 0);

    const rows = Array.from(table.querySelectorAll('tr'));
    rows.forEach((row) => {
      Array.from(row.querySelectorAll<HTMLElement>('td, th')).forEach(
        (cell, index) => {
          if (index < effectiveWidths.length) {
            const pct = ((effectiveWidths[index] / total) * 100).toFixed(2);
            // eslint-disable-next-line no-param-reassign
            cell.style.width = `${pct}%`;
          }
          cell.removeAttribute('colwidth');
        }
      );
    });
  });

  const div = document.createElement('div');
  div.appendChild(fragment);
  return div.innerHTML;
}

const customChromeConfig = {
  ADD_ATTR: ['target'],
  FORBID_TAGS: [
    'audio',
    'base',
    'embed',
    'form',
    'iframe',
    'input',
    'link',
    'meta',
    'object',
    'script',
    'select',
    'style',
    'textarea',
  ],
  KEEP_CONTENT: false,
  USE_PROFILES: { html: true, svg: true, svgFilters: true },
};

export const sanitizeCustomChrome = (val: string): string =>
  dompurifySanitize(val, customChromeConfig) as string;
